import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, Image as ImageIcon, Upload } from 'lucide-react';
import { api } from '@/lib/api';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import { toast } from 'sonner';

interface Photo { id: string; caption: string; tag: string; image_key: string; size: number; uploaded_at?: string }
interface Props { instructionId: string; token?: string; readOnly?: boolean }

const readFile = (file: File): Promise<string> => new Promise((res, rej) => {
  const reader = new FileReader();
  reader.onload = () => res(reader.result as string);
  reader.onerror = rej;
  reader.readAsDataURL(file);
});

export const PhotoGallery: React.FC<Props> = ({ instructionId, token, readOnly }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [busy, setBusy] = useState(false);

  const handle = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const dataUrl = await readFile(file);
        const client = token ? axios.create({ baseURL: API_BASE }) : api;
        const r = await client.post('/photos', { instruction_id: instructionId, caption: '', tag: '', image_data_url: dataUrl });
        setPhotos(p => [...p, { id: r.data.id, caption: '', tag: '', image_key: r.data.image_key, size: r.data.size }]);
      }
      toast.success(`Uploaded ${files.length} photo(s)`);
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Upload failed');
    } finally { setBusy(false); }
  };

  const del = async (id: string) => {
    if (readOnly) return;
    if (!confirm('Delete this photo?')) return;
    await api.delete(`/photos/${id}`);
    setPhotos(p => p.filter(x => x.id !== id));
  };

  return (
    <div className="space-y-3">
      {!readOnly && (
        <div className="flex items-center gap-2 p-3 border border-dashed border-raptor-blue/40 rounded-md bg-raptor-blue/5">
          <ImageIcon size={18} className="text-raptor-blue" />
          <span className="text-sm font-body text-raptor-navy/70 flex-1">Photo gallery. Optional captions per photo</span>
          <label className="cursor-pointer">
            <span className="inline-flex items-center gap-1 px-3 h-8 rounded-md bg-raptor-gradient text-white text-sm font-medium">
              <Upload size={14} /> {busy ? 'Uploading…' : 'Add photos'}
            </span>
            <input type="file" accept="image/*" multiple className="hidden" capture="environment" onChange={(e) => handle(e.target.files)} />
          </label>
        </div>
      )}
      {photos.length === 0 && <div className="text-sm text-raptor-navy/50 font-body text-center py-6">No photos yet.</div>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {photos.map(p => (
          <div key={p.id} className="rounded-md border border-border/60 overflow-hidden bg-white group relative">
            <div className="bg-raptor-neutral/40 h-28 flex items-center justify-center text-xs text-raptor-navy/60 font-tabular">{Math.round(p.size / 1024)} KB</div>
            <div className="p-2 space-y-1">
              <Input placeholder="Caption (optional)" defaultValue={p.caption} className="h-7 text-xs" onBlur={(e) => { p.caption = e.target.value; }} />
              {!readOnly && <Button size="sm" variant="ghost" onClick={() => del(p.id)} className="w-full"><Trash2 size={12} className="text-red-500" /></Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
export const photoIdsRef: { current: string[] } = { current: [] };
